// How the card forms that are not Airwallex's can be styled: Stripe's Payment
// Element, PayPal Card Fields and NomuPay's COPYandPAY widget.
//
// Each processor draws the card inside its own iframes and exposes a
// different, narrow styling surface, so each gets only the controls that
// surface actually reaches:
//
// - Stripe takes an Appearance object (`theme`, `inputs`, `labels`,
//   `variables`, `rules`, `disableAnimations`) and paints everything inside
//   one iframe itself.
// - PayPal takes CSS for the text inside each field iframe (`input`, `.valid`,
//   `.invalid`, `:focus`); the box around it is the checkout's own DOM.
// - COPYandPAY takes `wpwlOptions` (`style`, `showLabels`, `showPlaceholders`,
//   `showCVVHint`, `iframeStyles` for the two placeholders) and renders its
//   `.wpwl-*` classes into the page, where the checkout's CSS styles the box.
//
// Every value here is presentation. Nothing narrows the cards a buyer may pay
// with, and nothing reaches a confirm path. COPYandPAY's `brandDetection` is
// deliberately absent for that reason: it picks the scheme a co-badged card is
// charged on, which is the buyer's choice to make, not a style.
//
// The box vocabularies (chrome, type size, weight, focus ring) are the
// Airwallex section's, re-exported under neutral names, so a merchant styling
// two rails meets the same words and the checkout draws them the same way.

import {
  AIRWALLEX_FIELD_CHROMES,
  AIRWALLEX_FIELD_FONT_SIZES,
  AIRWALLEX_FIELD_WEIGHTS,
  AIRWALLEX_FOCUS_RINGS,
} from './airwallexCard';

/** How the box around a field is drawn: `outlined`, `filled` or `underline`. */
export const CARD_FIELD_CHROMES = AIRWALLEX_FIELD_CHROMES;
export type CardFieldChrome = (typeof CARD_FIELD_CHROMES)[number];

/** Field type size: `sm` 14px, `md` 16px, `lg` 18px. */
export const CARD_FIELD_FONT_SIZES = AIRWALLEX_FIELD_FONT_SIZES;
export type CardFieldFontSize = (typeof CARD_FIELD_FONT_SIZES)[number];

/** Field type weight: `regular` 400, `medium` 500, `semibold` 600. */
export const CARD_FIELD_WEIGHTS = AIRWALLEX_FIELD_WEIGHTS;
export type CardFieldWeight = (typeof CARD_FIELD_WEIGHTS)[number];

/** The ring around the focused field: `none`, `subtle` (neutral) or `strong` (brand colour). */
export const CARD_FOCUS_RINGS = AIRWALLEX_FOCUS_RINGS;
export type CardFocusRing = (typeof CARD_FOCUS_RINGS)[number];

/** A field type size in px. */
export function cardFieldFontPx(size: CardFieldFontSize): number {
  return size === 'sm' ? 14 : size === 'lg' ? 18 : 16;
}

/** A field type weight as a CSS numeric weight. */
export function cardFieldWeightValue(weight: CardFieldWeight): number {
  return weight === 'regular' ? 400 : weight === 'semibold' ? 600 : 500;
}

// --- Stripe Payment Element ---------------------------------------------

/**
 * Stripe's base theme. `auto` is `night` on a dark checkout and `stripe` on a
 * light one, which is what the pane chose before this setting existed. The
 * merchant's colours, font and radius are applied over whichever is picked.
 */
export const STRIPE_THEMES = ['auto', 'stripe', 'flat', 'night'] as const;
export type StripeTheme = (typeof STRIPE_THEMES)[number];

/** The theme Stripe is actually given. */
export function resolveStripeTheme(theme: StripeTheme, dark: boolean): 'stripe' | 'flat' | 'night' {
  if (theme === 'auto') return dark ? 'night' : 'stripe';
  return theme;
}

/** Stripe's `appearance.inputs`: separate boxes, or one condensed block. */
export const STRIPE_INPUT_STYLES = ['spaced', 'condensed'] as const;
export type StripeInputStyle = (typeof STRIPE_INPUT_STYLES)[number];

/**
 * Stripe's `appearance.labels`. The checkout-wide `labelStyle: 'hidden'` still
 * wins over either, because hiding is a decision about every form.
 */
export const STRIPE_LABEL_PLACEMENTS = ['above', 'floating'] as const;
export type StripeLabelPlacement = (typeof STRIPE_LABEL_PLACEMENTS)[number];

/** The space between Stripe's fields and rows. */
export const STRIPE_SPACINGS = ['compact', 'comfortable', 'spacious'] as const;
export type StripeSpacing = (typeof STRIPE_SPACINGS)[number];

/**
 * Stripe's `spacingUnit` and `gridRowSpacing` for a spacing choice.
 * `comfortable` is the 4px / 12px the pane always sent.
 */
export function stripeSpacingValues(spacing: StripeSpacing): {
  spacingUnit: string;
  gridRowSpacing: string;
} {
  if (spacing === 'compact') return { spacingUnit: '3px', gridRowSpacing: '8px' };
  if (spacing === 'spacious') return { spacingUnit: '5px', gridRowSpacing: '16px' };
  return { spacingUnit: '4px', gridRowSpacing: '12px' };
}

// --- PayPal Card Fields -------------------------------------------------

/** The PayPal card form's rows: expiry and CVC side by side, or every field full width. */
export const PAYPAL_CARD_LAYOUTS = ['split', 'stacked'] as const;
export type PaypalCardLayout = (typeof PAYPAL_CARD_LAYOUTS)[number];

/**
 * Captions over PayPal's fields. Visual only: a `<label for>` cannot reach an
 * input inside PayPal's iframe, and the field keeps the name PayPal gives it.
 */
export const PAYPAL_LABEL_STYLES = ['above', 'none'] as const;
export type PaypalLabelStyle = (typeof PAYPAL_LABEL_STYLES)[number];

// --- NomuPay COPYandPAY -------------------------------------------------

/**
 * COPYandPAY's `style`. `plain` is bare labelled fields; `card` is the
 * widget's card-shaped presentation. `logos` and `none` are not offered:
 * `logos` makes the buyer pick a brand before the fields appear, and `none`
 * drops the widget's own CSS the checkout's field styling builds on.
 */
export const NOMUPAY_FORM_STYLES = ['plain', 'card'] as const;
export type NomupayFormStyle = (typeof NOMUPAY_FORM_STYLES)[number];

/**
 * Apple Pay's `buttonStyle`. `auto` is `white` on a dark checkout and `black`
 * on a light one, as the widget was configured before this setting existed.
 */
export const APPLE_PAY_BUTTON_STYLES = ['auto', 'black', 'white', 'white-outline'] as const;
export type ApplePayButtonStyle = (typeof APPLE_PAY_BUTTON_STYLES)[number];

export function resolveApplePayButtonStyle(
  style: ApplePayButtonStyle,
  dark: boolean,
): 'black' | 'white' | 'white-outline' {
  if (style === 'auto') return dark ? 'white' : 'black';
  return style;
}

/** Apple Pay's `buttonType`: the word on the button. Apple's own list. */
export const APPLE_PAY_BUTTON_TYPES = [
  'pay',
  'plain',
  'buy',
  'check-out',
  'continue',
  'book',
  'order',
  'subscribe',
  'donate',
  'contribute',
  'support',
  'tip',
  'add-money',
  'top-up',
  'reload',
  'rent',
] as const;
export type ApplePayButtonType = (typeof APPLE_PAY_BUTTON_TYPES)[number];

/**
 * Google Pay's `buttonColor`. `auto` is `white` on a dark checkout and `black`
 * on a light one; `default` lets Google choose for the buyer's browser.
 */
export const GOOGLE_PAY_BUTTON_COLORS = ['auto', 'default', 'black', 'white'] as const;
export type GooglePayButtonColor = (typeof GOOGLE_PAY_BUTTON_COLORS)[number];

export function resolveGooglePayButtonColor(
  color: GooglePayButtonColor,
  dark: boolean,
): 'default' | 'black' | 'white' {
  if (color === 'auto') return dark ? 'white' : 'black';
  return color;
}

/** Google Pay's `buttonType`: the word on the button. Google's own list. */
export const GOOGLE_PAY_BUTTON_TYPES = [
  'pay',
  'plain',
  'buy',
  'checkout',
  'book',
  'order',
  'subscribe',
  'donate',
] as const;
export type GooglePayButtonType = (typeof GOOGLE_PAY_BUTTON_TYPES)[number];
